import { useEffect, useState } from "react"
import type { BadgeUdsContent } from "@gatsby-plugin-generic-page/fragments/badgeUds"
import { getMarkdownUdsBadge } from "@gatsby-plugin-generic-page/helpers/getMarkdownUdsBadge"
import { loadIcon } from "@ionos-web-design-system/icon/system/loader"
import isEmpty from "~/lib/isEmpty"

const ICON_SYNTAX_RE = /\[icon:[\w-]+\] ?/g
const ICON_NAME_RE = /\[icon:([\w-]+)\]/g
const HAS_ICON_RE = /\[icon:[\w-]+\]/

// Removes icon syntax from markdown until icons are loaded, to avoid showing raw syntax to users.
// If there are no icons in the markdown, it returns the original markdown immediately.
export function useLoadedProductBoxTitleMarkdown(markdown: string): string {
  const [isLoadedIcons, setIsLoadedIcons] = useState(false)

  useEffect(() => {
    const iconNames = Array.from(
      markdown.matchAll(ICON_NAME_RE),
      (match) => match[1]
    )

    if (isEmpty(iconNames)) {
      setIsLoadedIcons(true)

      return
    }

    Promise.all(
      iconNames.map((iconName) => loadIcon(iconName).catch(() => null))
    ).then(() => setIsLoadedIcons(true))
  }, [markdown])

  return isLoadedIcons || !HAS_ICON_RE.test(markdown)
    ? markdown
    : markdown.replace(ICON_SYNTAX_RE, "")
}

// Builds markdown for product box title, combining title with discount label and other labels as badges.
// If there are no labels, it returns the original title.
function buildMarkdown(
  title: string,
  discountLabel?: string,
  labels?: BadgeUdsContent[]
): string {
  const discountBadge = !isEmpty(discountLabel)
    ? getMarkdownUdsBadge({ text: discountLabel, variant: "promo" })
    : null

  const labelsBadges = !isEmpty(labels)
    ? labels.map((label) => getMarkdownUdsBadge(label.flatData)).join(" ")
    : null

  const prefix = [discountBadge, labelsBadges].filter(Boolean).join(" ")

  return prefix ? `${prefix}\n${title}` : title
}

// Custom hook to get product box title with labels as markdown.
// It combines title with discount label and other labels, and processes icons in the markdown.
export default function useProductBoxTitleWithLabels(
  title: string,
  discountLabel?: string,
  labels?: BadgeUdsContent[]
): string {
  return useLoadedProductBoxTitleMarkdown(
    buildMarkdown(title, discountLabel, labels)
  )
}
